<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog/index.js'
  import * as Select from '$lib/components/ui/select/index.js'
  import * as Popover from '$lib/components/ui/popover/index.js'

  import FolderIcon from '@lucide/svelte/icons/folder'
  import ChevronsUpDownIcon from '@lucide/svelte/icons/chevrons-up-down'
  import ChevronRightIcon from '@lucide/svelte/icons/chevron-right'
  import ArrowUpIcon from '@lucide/svelte/icons/arrow-up'
  import AlertCircleIcon from '@lucide/svelte/icons/alert-circle'

  import { Label } from '$lib/components/ui/label/index.js'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Button } from '$lib/components/ui/button/index'
  import { tick } from 'svelte'
  import { servers } from '$lib/store'
  import { requestServer } from '$lib/servers.svelte'
  import { createLibrary, scanAndRefreshLibraries, TRANSLATION_SOURCE_LANGUAGES, libraryLanguageLabel, languageToSelect, languageFromSelect } from '$lib/apis/library/api'
  import { trackOperation, operationTitle } from '$lib/operations.svelte'
  import { formatServerName } from '$lib/utils'

  function serverFor(id: string) {
    return ($servers as Record<string, unknown>)[id]
  }

  let {
    open = $bindable(false),
    serverid,
    refreshLibraries,
  }: {
    open?: boolean
    serverid: string
    refreshLibraries: (serverUid: string) => Promise<void>
  } = $props()

  let creating = $state(false)
  let error = $state<string | null>(null)

  let form = $state({
    name: '' as string,
    kind: null as string | null,
    language: 'en' as string,
    path: null as string | null,
  })

  const kinds = ['manga', 'novel', 'webtoon', 'webnovel']
  const triggerContent = $derived(form.kind ?? 'choose your library kind…')
  const languageLabel = $derived(libraryLanguageLabel(form.language))

  /** Directory being browsed; always uses trailing slash except root is `/` */
  let browseDir = $state('/')
  let folderNames = $state<string[]>([])
  let pathFilter = $state('')
  let pathLoading = $state(false)
  let pathLoadError = $state<string | null>(null)

  let pathPickerOpen = $state(false)
  let pathTriggerRef = $state<HTMLButtonElement>(null!)

  $effect(() => {
    if (!open) {
      form = { name: '', kind: null, language: 'en', path: null }
      error = null
      creating = false
      pathPickerOpen = false
      browseDir = '/'
      pathFilter = ''
      folderNames = []
      pathLoadError = null
    }
  })

  function onOpenChange(next: boolean) {
    if (!next && pathPickerOpen) {
      pathPickerOpen = false
      open = true
      return
    }
    open = next
  }

  function normalizeBrowseDir(p: string): string {
    const collapsed = p.replace(/\/{2,}/g, '/')
    if (!collapsed || collapsed === '/') return '/'
    return collapsed.endsWith('/') ? collapsed : `${collapsed}/`
  }

  function parentBrowseDir(dir: string): string | null {
    const d = normalizeBrowseDir(dir)
    const core = d.replace(/\/+$/, '')
    if (core === '' || core === '/') return null
    const i = core.lastIndexOf('/')
    if (i <= 0) return '/'
    return `${core.slice(0, i)}/`
  }

  function joinDirName(dir: string, name: string): string {
    const base = normalizeBrowseDir(dir).replace(/\/+$/, '') || '/'
    if (base === '/') return `/${name}/`
    return `${base}/${name}/`
  }

  /** Path string stored on the library (no trailing slash except root `/`) */
  function browseDirToFormPath(dir: string): string {
    const d = normalizeBrowseDir(dir)
    if (d === '/') return '/'
    return d.replace(/\/+$/, '')
  }

  const pathTriggerLabel = $derived(
    form.path != null && form.path !== ''
      ? form.path
      : 'Choose folder on server…'
  )

  const canSubmit = $derived(
    !!form.name.trim() && !!form.kind && form.path != null && form.path !== ''
  )

  const filteredFolders = $derived(
    pathFilter.trim() === ''
      ? folderNames
      : folderNames.filter((n) =>
          n.toLowerCase().includes(pathFilter.trim().toLowerCase())
        )
  )

  const INSUFFICIENT_ROLE_MSG =
    'You need the Owner role on this server to browse folders. Only owners can add libraries—ask the server owner to promote you, or claim the server if it is yours.'

  function pathBrowseError(err: unknown): string {
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 403) return INSUFFICIENT_ROLE_MSG
    return 'Could not read this folder. (insufficient role)'
  }

  async function loadFolderListing(dir: string) {
    pathLoading = true
    pathLoadError = null
    try {
      const server = serverFor(serverid)
      const { data } = await requestServer(
        server,
        'post',
        'api/v1/readpath',
        { path: normalizeBrowseDir(dir) },
      )
      const r = data as { subpaths?: string[] } | null
      folderNames = [...(r?.subpaths ?? [])].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      )
    } catch (err) {
      pathLoadError = pathBrowseError(err)
      folderNames = []
    } finally {
      pathLoading = false
    }
  }

  let pathPickerWasOpen = false
  $effect(() => {
    if (!pathPickerOpen) {
      pathPickerWasOpen = false
      return
    }
    if (!pathPickerWasOpen) {
      pathFilter = ''
      if (form.path != null && form.path !== '') {
        const p = form.path === '/' ? '/' : `${form.path.replace(/\/+$/, '')}/`
        browseDir = normalizeBrowseDir(p)
      } else {
        browseDir = '/'
      }
    }
    pathPickerWasOpen = true
    void serverFor(serverid)
    void loadFolderListing(browseDir)
  })

  function goUp() {
    const p = parentBrowseDir(browseDir)
    if (p != null) browseDir = p
  }

  function enterFolder(name: string) {
    browseDir = joinDirName(browseDir, name)
    pathFilter = ''
  }

  function confirmCurrentFolder() {
    form.path = browseDirToFormPath(browseDir)
    pathPickerOpen = false
    tick().then(() => pathTriggerRef?.focus())
  }

  function pathSegments(): { label: string; dir: string }[] {
    const full = browseDirToFormPath(browseDir)
    if (full === '/') return [{ label: '/', dir: '/' }]
    const parts = full.split('/').filter(Boolean)
    const out: { label: string; dir: string }[] = [{ label: '/', dir: '/' }]
    let acc = ''
    for (const part of parts) {
      acc = `${acc}/${part}`
      out.push({ label: part, dir: normalizeBrowseDir(acc) })
    }
    return out
  }

  function jumpToSegment(dir: string) {
    browseDir = normalizeBrowseDir(dir)
    pathFilter = ''
  }

  async function onSubmit(e: Event) {
    e.preventDefault()
    if (creating || !canSubmit) return
    creating = true
    error = null
    try {
      const server = serverFor(serverid)
      await createLibrary(
        server,
        form.name.trim(),
        form.kind!,
        form.path!,
        languageFromSelect(form.language),
      )
      await refreshLibraries(serverid)
      const data = await scanAndRefreshLibraries(server)
      trackOperation({
        server,
        operation: data?.operation ?? {},
        title: operationTitle(data?.operation?.type, 'Scanning & refreshing libraries'),
        description: formatServerName(
          (server as { name?: string })?.name,
          serverid,
        ),
      })
      open = false
    } catch (err) {
      console.error('create library failed', err)
      error =
        err instanceof Error && err.message
          ? err.message
          : 'Could not create the library. Try again.'
    } finally {
      creating = false
    }
  }
</script>

<Dialog.Root {open} onOpenChange={onOpenChange}>
  <Dialog.Content
    class="border-latte-surface1/60 bg-latte-mantle/95 sm:max-w-md
      dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
  >
    <Dialog.Header>
      <Dialog.Title class="text-latte-text dark:text-macchiato-text">
        Add a Library
      </Dialog.Title>
      <Dialog.Description class="text-latte-text/65 dark:text-macchiato-text/65">
        Name it, pick a kind, and choose the folder on this server that holds your
        books.
      </Dialog.Description>
    </Dialog.Header>

    <form class="flex flex-col gap-4" onsubmit={onSubmit}>
      <div class="flex flex-col gap-2">
        <Label for="create-library-name">Name</Label>
        <Input
          id="create-library-name"
          bind:value={form.name}
          placeholder="name your library…"
          autocomplete="off"
          required
          disabled={creating}
        />
      </div>

      <div class="flex flex-col gap-2">
        <Label for="create-library-kind">Kind</Label>
        <Select.Root type="single" name="kind" bind:value={form.kind} disabled={creating}>
          <Select.Trigger id="create-library-kind" class="w-full">
            {triggerContent}
          </Select.Trigger>
          <Select.Content class="z-62">
            <Select.Group>
              <Select.Label>library kind</Select.Label>
              {#each kinds as kind (kind)}
                <Select.Item value={kind} label={kind}>
                  {kind}
                </Select.Item>
              {/each}
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </div>

      <div class="flex flex-col gap-2">
        <Label for="create-library-language">Source language</Label>
        <p class="text-xs leading-snug text-latte-text/60 dark:text-macchiato-text/60">
          Language of the books — used to decide if translation is needed.
        </p>
        <Select.Root
          type="single"
          name="language"
          value={languageToSelect(form.language)}
          onValueChange={(v) => {
            form.language = languageFromSelect(v)
          }}
          disabled={creating}
        >
          <Select.Trigger id="create-library-language" class="w-full">
            {languageLabel}
          </Select.Trigger>
          <Select.Content class="z-62">
            <Select.Group>
              <Select.Label>source language</Select.Label>
              {#each TRANSLATION_SOURCE_LANGUAGES as opt (opt.value)}
                <Select.Item value={opt.value} label={opt.label}>
                  {opt.label}
                </Select.Item>
              {/each}
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </div>

      <div class="flex flex-col gap-2">
        <Label id="path-label">Library folder</Label>
        <p
          class="text-xs leading-snug text-latte-text/60 dark:text-macchiato-text/60"
          id="path-hint"
        >
          Browse directories on the server, then confirm the folder that contains your
          books.
        </p>
        <Popover.Root bind:open={pathPickerOpen}>
          <Popover.Trigger bind:ref={pathTriggerRef}>
            {#snippet child({ props })}
              <Button
                {...props}
                variant="outline"
                class="h-auto min-h-9 w-full justify-between gap-2 py-2 text-left font-normal"
                role="combobox"
                aria-expanded={pathPickerOpen}
                aria-labelledby="path-label path-hint"
                disabled={creating}
              >
                <span class="line-clamp-2 break-all">{pathTriggerLabel}</span>
                <ChevronsUpDownIcon class="size-4 shrink-0 opacity-50" />
              </Button>
            {/snippet}
          </Popover.Trigger>
          <Popover.Content
            class="z-62 flex w-[min(calc(100vw-2rem),22rem)] flex-col gap-2 border-latte-surface1/60
              bg-latte-mantle p-3 dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle"
            align="start"
          >
            <div
              class="flex flex-wrap items-center gap-1 border-b border-latte-surface1/60 pb-2
                dark:border-macchiato-surface1/60"
            >
              {#each pathSegments() as seg, i (seg.dir)}
                {#if i > 0}
                  <span
                    class="text-xs text-latte-text/50 dark:text-macchiato-text/50"
                    aria-hidden="true"
                  >/</span>
                {/if}
                <button
                  type="button"
                  class="max-w-[8rem] truncate text-xs font-medium text-latte-green
                    hover:underline dark:text-macchiato-green"
                  onclick={() => jumpToSegment(seg.dir)}
                >
                  {seg.label}
                </button>
              {/each}
            </div>

            <div class="flex gap-2">
              <Input
                bind:value={pathFilter}
                placeholder="Filter folders…"
                class="flex-1"
                aria-label="Filter folders in this directory"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                class="shrink-0"
                disabled={parentBrowseDir(browseDir) === null}
                onclick={goUp}
                aria-label="Parent folder"
              >
                <ArrowUpIcon class="size-4" />
              </Button>
            </div>

            <div
              class="max-h-56 overflow-y-auto rounded-md border border-latte-surface1/60
                bg-latte-base/50 text-sm dark:border-macchiato-surface1/60
                dark:bg-macchiato-base/50"
              role="listbox"
              aria-label="Subfolders"
              aria-busy={pathLoading}
            >
              {#if pathLoading}
                <p class="p-3 text-xs text-latte-text/60 dark:text-macchiato-text/60">
                  Loading…
                </p>
              {:else if pathLoadError}
                <p class="p-3 text-xs text-latte-red dark:text-macchiato-red">
                  {pathLoadError}
                </p>
              {:else if filteredFolders.length === 0}
                <p class="p-3 text-xs text-latte-text/60 dark:text-macchiato-text/60">
                  {folderNames.length === 0
                    ? 'No subfolders here. Use “Use this folder” if this is the library root.'
                    : 'No folders match your filter.'}
                </p>
              {:else}
                {#each filteredFolders as name (name)}
                  <button
                    type="button"
                    class="flex w-full items-center gap-2 px-3 py-2 text-left
                      hover:bg-latte-surface1/40 dark:hover:bg-macchiato-surface1/40"
                    onclick={() => enterFolder(name)}
                  >
                    <FolderIcon
                      class="size-4 shrink-0 text-latte-text/50 dark:text-macchiato-text/50"
                    />
                    <span class="min-w-0 flex-1 truncate">{name}</span>
                    <ChevronRightIcon
                      class="size-4 shrink-0 text-latte-text/50 dark:text-macchiato-text/50"
                    />
                  </button>
                {/each}
              {/if}
            </div>

            <Button
              type="button"
              class="w-full bg-latte-green text-white hover:bg-latte-green/90
                dark:bg-macchiato-green dark:text-macchiato-base
                dark:hover:bg-macchiato-green/90"
              onclick={confirmCurrentFolder}
            >
              Use this folder
            </Button>
          </Popover.Content>
        </Popover.Root>
        {#if pathLoadError}
          <div
            class="flex items-start gap-2 rounded-md border border-latte-red/35
              bg-latte-red/8 px-3 py-2 text-xs text-latte-red
              dark:border-macchiato-red/35 dark:bg-macchiato-red/10
              dark:text-macchiato-red"
            role="alert"
          >
            <AlertCircleIcon class="mt-0.5 size-3.5 shrink-0" strokeWidth={1.75} />
            <p class="min-w-0 leading-snug">{pathLoadError}</p>
          </div>
        {/if}
      </div>

      {#if error}
        <div
          class="flex items-start gap-2 rounded-md border border-latte-red/35
            bg-latte-red/8 px-3 py-2 text-sm text-latte-red
            dark:border-macchiato-red/35 dark:bg-macchiato-red/10
            dark:text-macchiato-red"
          role="alert"
        >
          <AlertCircleIcon class="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
          <p class="min-w-0 leading-snug">{error}</p>
        </div>
      {/if}

      <Dialog.Footer class="gap-2 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={creating}
          onclick={() => (open = false)}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          class="bg-latte-green text-white hover:bg-latte-green/90
            dark:bg-macchiato-green dark:text-macchiato-base
            dark:hover:bg-macchiato-green/90"
          disabled={creating || !canSubmit}
        >
          {creating ? 'Creating…' : 'Create'}
        </Button>
      </Dialog.Footer>
    </form>
  </Dialog.Content>
</Dialog.Root>
